import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import IconChevron from "@/assets/icons/chevron.svg?react";
import IconUserCircle from "@/assets/icons/user-circle.svg?react";
import IconRocket from "@/assets/icons/rocket.svg?react";
import IconDonFilledLinear from "@/assets/icons/don-filled-linear.svg?react";
import IconDon from "/public/don.svg?react";
import IconInfoCircle from "@/assets/icons/info-circle.svg?react";
import IconDiamondFilledLinear from "@/assets/icons/diamond-filled-linear.svg?react";
import IconX from "@/assets/icons/x-mark.svg?react";
import IconClock from "@/assets/icons/clock.svg?react";

import ExpandableText from "@/components/common/ExpandableText/ExpandableText.tsx";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import ModalShareChallenge from "./components/modals/ModalShareChallenge.tsx";
import ChallengesSingleSlider from "./ChallengesSingleSlider.tsx";
import ChallengeInfo from "./ChallengeInfo.tsx";
import SingleChallengeLoading from "./SingleChallengeLoading";
import PageWrapper from "../../components/PageWrapper.tsx";
import { ErrorBoundary } from "react-error-boundary";
import { Fragment, Suspense, useEffect, useState } from "react";
import { logError } from "@/components/error-hanlder/error-handler.lib.ts";
import {
  useChallengeDetail,
  useChallengeParticipateMutation, useStaticPageDetail
} from "@/pages/challenges/api/useChallengeDetail.ts";
import {
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { formatNumber } from "@/shared/utils";
import { ChallengeStatus } from "@/pages/challenges/types";
import ChallengeRuleDrawer from "@/pages/challenges/ChallengeRuleDrawer.tsx";
import { useGetProfile } from "@/pages/profile/api";
import BuyMoreChances from "@/pages/challenges/buy-chances/BuyMoreChances.tsx";
import { useChallengeMembers } from "@/pages/challenges/api/challenge-members.api.ts";
import { ChallengeMemberList } from "@/pages/challenges/SingleParticipants.tsx";

import dayjs from "dayjs";
import { cn } from "@/lib/utils.ts";
import ParticipantCard from "./components/ParticipantCard.tsx";
import Divider from "@/components/common/Divider.tsx";
import { useReferralCode } from "@/pages/referral/api";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { toast } from "sonner";
import JoinChallenge from "./JoinChallenge.tsx";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog.tsx";
import UserCardLoading from "./components/UserCardLoading.tsx";
import { useInfiniteQueryFlatted } from "@/shared/hooks/useInfiniteQueryFlatted.ts";
import InfiniteScrollList, {
  checkFetchMore,
} from "@/components/base/InfiniteScrollList.tsx";

interface DonReturnedSoonInfoProps {
  className?: string;
}

const ErrorHandler = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    toast.error(t("data_fetching_error"));
    navigate("/challenges");
  }, [t, navigate]);

  return null;
};

export const DonReturnedSoonInfo = (props: DonReturnedSoonInfoProps) => {
  const { t } = useTranslation();
  const { className } = props;

  return (
    <p
      className={cn(
        "p-2 rounded-xl bg-white/08 mb-3 flex-y-center gap-2 text-xs leading-130 font-normal furgor text-white",
        className
      )}
    >
      <IconInfoCircle className="text-gray-1" width="20" height="20" />
      {t("dons_will_be_returned_to_the_participants_soon")}
    </p>
  );
};

interface ChallengeInfoConfig {
  showChance: boolean;
  showDeadline: boolean;
  showParticipantsCount: boolean;
  showRules: boolean;
  showProofs: boolean;
  showFooterButton: boolean;
}

const CHALLENGE_INFO_CONFIG: Record<ChallengeStatus, ChallengeInfoConfig> = {
  [ChallengeStatus.DRAFT]: {
    showChance: false,
    showDeadline: false,
    showParticipantsCount: false,
    showRules: false,
    showProofs: false,
    showFooterButton: false,
  },
  [ChallengeStatus.ACTIVE]: {
    showChance: true,
    showDeadline: true,
    showParticipantsCount: true,
    showRules: true,
    showProofs: false,
    showFooterButton: true,
  },
  [ChallengeStatus.STARTED]: {
    showChance: true,
    showDeadline: true,
    showParticipantsCount: true,
    showRules: true,
    showProofs: false,
    showFooterButton: true,
  },
  [ChallengeStatus.DETERMINING_WINNERS]: {
    showChance: false,
    showDeadline: false,
    showParticipantsCount: true,
    showRules: true,
    showProofs: false,
    showFooterButton: true,
  },
  [ChallengeStatus.CANCELED]: {
    showChance: false,
    showDeadline: false,
    showParticipantsCount: true,
    showRules: true,
    showProofs: false,
    showFooterButton: false,
  },
  [ChallengeStatus.COMPLETED]: {
    showChance: false,
    showDeadline: false,
    showParticipantsCount: true,
    showRules: true,
    showProofs: true,
    showFooterButton: false,
  },
  [ChallengeStatus.COMING_SOON]: {
    showChance: false,
    showDeadline: false,
    showParticipantsCount: false,
    showRules: false,
    showProofs: false,
    showFooterButton: true,
  },
  [ChallengeStatus.FAILED]: {
    showChance: false,
    showDeadline: false,
    showParticipantsCount: false,
    showRules: true,
    showProofs: false,
    showFooterButton: false,
  },
};

const ChallengesSingle = () => {
  const [referralCode, setReferralCode] = useState<string | undefined>();
  const referralCodeMutation = useReferralCode();
  const { data: profile } = useGetProfile();
  useEffect(() => {
    if (profile && profile.referral_code) {
      setReferralCode(profile.referral_code);
    } else {
      referralCodeMutation.mutate(undefined, {
        onSuccess: (res) => {
          setReferralCode(res.referral_code);
        },
      });
    }
  }, [profile, referralCodeMutation]);

  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense fallback={<SingleChallengeLoading />}>
        <BaseChallengesSingle referralCode={referralCode} />
      </Suspense>
    </ErrorBoundary>
  );
};

const BaseChallengesSingle = (props: { referralCode: string | undefined }) => {
  const { challengeId } = useParams();
  const { data: challengeData } = useSuspenseQuery(useChallengeDetail(challengeId));

  // if (challengeData.status === ChallengeStatus.COMPLETED) {
  //   return (
  //       <PageWrapper>
  //         <ChallengeAwardDetails />
  //       </PageWrapper>
  //   )
  // }

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: profile } = useGetProfile();
  const { data: staticPageData } = useQuery(useStaticPageDetail(challengeId));

  const queryResult = useSuspenseInfiniteQuery(
    useChallengeMembers(challengeId, undefined, true)
  );

  const members = useInfiniteQueryFlatted(queryResult.data);
  const { referralCode } = props;

  const { mutate: participate, isPending: isParticipating } =
    useChallengeParticipateMutation(challengeId);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleParticipate = async () => {
    if (challengeData.is_participating) {
      showCustomToast(t("you_are_already_participating"), "error");
      return;
    }

    if (
      profile?.wallet_amount &&
      parseFloat(profile?.wallet_amount) < parseFloat(challengeData.participant_fee)
    ) {
      showCustomToast(t("lack_of_don_to_participate"), "error");
      return;
    }

    participate(undefined, {
      onSuccess: () => {
        setIsModalOpen(false);
      },
    });
  };

  const challengeInfoConfig = CHALLENGE_INFO_CONFIG[challengeData.status];

  const activeStatuses = [
    ChallengeStatus.ACTIVE,
    ChallengeStatus.STARTED,
    ChallengeStatus.DETERMINING_WINNERS,
  ];
  return (
    <PageWrapper backPath="/challenges">
      <div
        className={cn("relative min-h-screen", {
          "pb-[calc(8px+76px)]": challengeInfoConfig.showFooterButton,
          "pb-4": !challengeInfoConfig.showFooterButton,
        })}
      >
        <ChallengesSingleSlider
          medias={challengeData.images}
        />
        <div className="container">
          <h1 className="mt-5 mb-2 text-xl font-bold leading-130 text-white">
            {challengeData.name}
          </h1>
          <ExpandableText
              text={challengeData.description || t("no_description")}
              className="text-xs text-white font-normal leading-140 mb-4"
            />
          {challengeData.status !== ChallengeStatus.COMING_SOON && (
  <ChallengeInfo
    status={challengeData.status}
    gainedAmount={challengeData.gained_amount}
    targetAmount={challengeData.target_amount}
    deadline={challengeData.deadline}
    youtubeLink={challengeData.youtube_link}
  />
          )}
          <div
            className={`bg-white/08 rounded-xl border-b border-white/08 my-4 px-3 backdrop-blur-[10px] ${
              challengeData.status === ChallengeStatus.COMING_SOON ? "hidden" : ""
            }`}
          >
            {challengeInfoConfig.showChance && (
              <ChallengeInfoTable
                title={t("chance")}
                value={t("challenge.challenge_price_per_opportunity", {
                  don: formatNumber(challengeData.opportunity_fee),
                })}
              />
            )}
            {challengeData.is_participating && (
              <ChallengeInfoTable
                title={t("participate_confirm")}
                value={`${formatNumber(challengeData.participant_fee)} DON`}
              />
            )}
            {challengeInfoConfig.showDeadline && (
              <ChallengeInfoTable
                title={t("deadline")}
                value={dayjs(challengeData.deadline).format("DD MMMM YYYY")}
              />
            )}
            {challengeInfoConfig.showParticipantsCount &&
              challengeData.is_participating && (
                <ChallengeInfoTable
                  title={t("participants_count")}
                  value={challengeData.participants_count?.toString()}
                />
              )}
            {challengeInfoConfig.showRules && (
              <ChallengeInfoTable
                title={t("rules_of_challenge")}
                value={t("more_details")}
                ruleInfo={challengeData.conditions}
                showRule
              />
            )}
            {challengeInfoConfig.showProofs && staticPageData && (
              <ChallengeInfoTable
                title={t("about_the_award")}
                value={t("more_details")}
                ruleInfo={challengeData.conditions}
                showRule
                navigateTo={
                  challengeData.status === ChallengeStatus.COMPLETED
                    ? `/challenges/${challengeId}/award-details`
                    : undefined
                }
              />
            )}
            {challengeData.status === ChallengeStatus.CANCELED && (
              <DonReturnedSoonInfo />
            )}
          </div>
          {challengeData.status !== ChallengeStatus.COMING_SOON && (
            <Fragment>
              {challengeData.is_participating && (
                <MyInfoInTheChallenge
                  rank={challengeData.my_position}
                  chances={challengeData.my_opportunities}
                />
              )}
            </Fragment>
          )}
        </div>
        {challengeData.status !== ChallengeStatus.COMING_SOON && (
          <Fragment>
            {challengeData.is_participating ? (
              <InfiniteScrollList
                canFetchMore={checkFetchMore(queryResult)}
                isFetchingNextPage={queryResult.isFetchingNextPage}
                fetchNextPage={queryResult.fetchNextPage}
                className="container gap-3"
                placeholder={
                  <>
                    {Array.from({ length: 1 }).map((_, idx) => (
                      <UserCardLoading key={idx} />
                    ))}
                  </>
                }
              >
                {members?.length && members.length > 0 ? (
                  <div className="">
                    <div className="flex justify-between items-center gap-2 mb-3">
                      <h2 className="text-lg font-bold leading-130 text-white">
                        {challengeData.status === ChallengeStatus.COMPLETED
                          ? t("gift_owners")
                          : t("participants")}
                      </h2>
                      <VibratingButton
                        onClick={() =>
                          navigate(
                            challengeData.status === ChallengeStatus.COMPLETED
                              ? "winners"
                              : "members"
                          )
                        }
                        className="text-gray-1 text-xs font-medium leading-130 flex-y-center gap-0.5"
                      >
                        {t("all_button")}
                        <IconChevron width="18" height="18" strokeWidth="1.6" />
                      </VibratingButton>
                    </div>
                    <div className="flex flex-col gap-3">
                      {challengeData.super_winner && (
                        <>
                          <ParticipantCard
                            avatar={challengeData.super_winner.avatar}
                            fullName={challengeData.super_winner.full_name}
                            isMe={challengeData.super_winner.id === profile?.id}
                            isPremium={challengeData.super_winner.is_premium}
                            username={challengeData.super_winner.username}
                            showSeperator={false}
                            chance={challengeData.super_winner.opportunities}
                            isCompleted
                            gift={challengeData.super_award}
                          />
                          <Divider />
                        </>
                      )}
                      <ChallengeMemberList
                        members={members}
                        isCompleted={challengeData.status === ChallengeStatus.COMPLETED}
                        showSeperator={false}
                      />
                    </div>
                  </div>
                ) : null}
              </InfiniteScrollList>
            ) : activeStatuses.includes(challengeData.status) ? (
              <div className="container mb-2">
                <h1 className="mb-3 font-bold leading-130 text-lg text-white">
                  {t("participants")}
                </h1>
                <JoinChallenge />
              </div>
            ) : null}
          </Fragment>
        )}
        {challengeInfoConfig.showFooterButton && (
          <div className="fixed left-0 right-0 bottom-0 z-10">
            <div className="container pr-6 pb-safe-bottom pt-4 bg-[linear-gradient(180deg,#0E0E1400_0%,_#0E0E14_47.8%)]">
              {challengeData?.is_premium && (
                <p className="mb-3 flex-center gap-1 text-xs font-medium leading-130 text-white">
                  <IconDiamondFilledLinear width="20" height="20" />
                  {t("join_is_for_premium_users")}
                </p>
              )}
              <div className="flex items-center gap-2">
                <ModalShareChallenge
                  targetDon={challengeData.target_amount}
                  gainedDon={challengeData.gained_amount}
                  status={challengeData?.status}
                  referralCode={referralCode ?? ""}
                />
                {challengeData.is_participating && challengeId ? (
                  <BuyMoreChances
                    isPremium={challengeData.is_premium}
                    chanceFee={challengeData.opportunity_fee}
                    challengeId={challengeId}
                  />
                ) : challengeData?.is_premium && !profile?.is_premium ? (
                  <Link className="w-full " to={"/profile/premium"}>
                    <BaseButton
                      variant="premium"
                      text={t("go_to_buy_premium")}
                      className="w-full shadow-[0_4.442px_22.208px_0_rgba(251,199,108,0.47)]"
                    />
                  </Link>
                ) : challengeData.status === ChallengeStatus.COMING_SOON ? (
                  <VibratingButton className="w-full py-2.5 flex items-center gap-1 justify-center !bg-[rgba(255,255,255,0.12)] backdrop-blur-md text-white rounded-lg">
                    <p className="leading-130 text-sm font-semibold text-gray-1">
                      {t("soon")}
                    </p>
                    <IconClock className="text-gray-1 w-6 h-6" />
                  </VibratingButton>
                ) : (
                  <BaseButton
                    text={t("join")}
                    onClick={handleOpenModal}
                    disabled={isParticipating}
                    suffix={
                      <div className="p-1 pr-1.5 flex-y-center gap-1 bg-black rounded-lg text-white text-sm font-bold leading-130">
                        <IconDonFilledLinear width="20" height="20" />
                        {formatNumber(challengeData.participant_fee)}
                      </div>
                    }
                    className="w-full h-11 shadow-[0_4.442px_22.208px_0_rgba(37,133,255,0.47)]"
                  />
                )}
              </div>
            </div>
          </div>
        )}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="bg-[linear-gradient(150deg,_#45454D_0%,_#14141E_72.24%)] flex flex-col justify-center items-center max-w-[311px] !rounded-20 !p-4 border-2 border-white/08 text-white">
            <img
              src="images/patterns/check-in-modal.webp"
              className="absolute w-[194px] -top-24"
              alt=""
            />
            <div
              className="p-1.5 rounded-full bg-white/08 absolute top-4 right-4 "
              onClick={handleCloseModal}
            >
              <IconX width="16" height="16" />
            </div>
            <div className="flex justify-center mt-8 items-center gap-1">
              <IconDon className="" width="24" height="24" />
              <p className="font-extrabold text-2xl text-center">
                {formatNumber(challengeData.participant_fee)}
              </p>
            </div>
            <p className="h-px w-full bg-white/08"></p>
            <DialogTitle className="text-xs text-white w-[80%] text-center leading-130 font-normal">
              {t("join_challenge")}
            </DialogTitle>
            <div className="flex w-full items-center gap-2 justify-center">
              <BaseButton
                text={t("cancel")}
                onClick={handleCloseModal}
                variant="secondary"
                className="!w-full text-xs leading-140 whitespace-nowrap font-semibold "
              />
              <BaseButton
                text={t("participation")}
                onClick={handleParticipate}
                disabled={isParticipating}
                prefix={
                  isParticipating && (
                    <img
                      width="20"
                      height="20"
                      src="/loader.svg"
                      alt="loading"
                      className="animate-spin-slow"
                    />
                  )
                }
                className="w-full !text-xs leading-140 whitespace-nowrap font-semibold"
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PageWrapper>
  );
};

interface ChallengeInfoTableProps {
  title: string;
  value: string;
  showRule?: boolean;
  ruleInfo?: string;
  navigateTo?: string;
}

const ChallengeInfoTable = (props: ChallengeInfoTableProps) => {
  const { title, value, showRule, ruleInfo, navigateTo } = props;
  const navigate = useNavigate();

  const handleRowClick = () => {
    if (navigateTo) {
      navigate(navigateTo);
    }
  }
  return (
    <div
      className={cn(
        "flex-center-between py-3 border-b border-white/08 last-of-type:border-none",
        { "cursor-pointer": !!navigateTo || showRule }
      )}
      onClick={navigateTo ? handleRowClick : undefined}
    >
      <p className="text-xs font-normal leading-130 text-gray-1">{title}</p>
      {showRule && ruleInfo ? (
        <ChallengeRuleDrawer info={ruleInfo} />
      ) : (
        <p className="text-xs font-medium leading-130 text-white flex-y-center">
          {value}
          {showRule && (
            <IconChevron
              width="16"
              height="16"
              className="text-gray-1"
              strokeWidth="1.6"
            />
          )}
        </p>
      )}
    </div>
  );
};
interface MyInfoInTheChallengeProps {
  chances: number;
  rank: number | string;
}

const MyInfoInTheChallenge = (props: MyInfoInTheChallengeProps) => {
  const { chances, rank } = props;

  const { data: me } = useGetProfile();

  const { t } = useTranslation();
  return (
    <div className="bg-white/08 rounded-10 border-b border-white/08 backdrop-blur-[10px] mb-4">
      <div className="p-3 flex-y-center gap-2 relative">
        <div className="w-8 h-8 rounded-lg bg-red shrink-0 flex-center">
          <IconUserCircle width="24" height="24" />
        </div>
        <div className="w-full flex flex-col gap-0.5">
          <p className="text-sm font-semibold leading-4 text-white">
            {me?.full_name || "Anonymous"}
          </p>
          <p className="text-gray-1 text-xs font-normal leading-130">
            {t("place_in_challenge")}
          </p>
        </div>
        <p className="shrink-0 text-base font-semibold leading-normal text-white">
          {formatNumber(Number(rank))}
        </p>
        <hr className="absolute bottom-0 right-0 border-none bg-white/08 h-px w-[calc(100%-52px)]" />
      </div>
      <div className="p-3 flex-y-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-green shrink-0 flex-center">
          <IconRocket width="24" height="24" />
        </div>
        <div className="w-full flex flex-col gap-0.5">
          <p className="text-sm font-semibold leading-4 text-white">
            {t("chances")}
          </p>
          <p className="text-gray-1 text-xs font-normal leading-130">
            {t("chances_for_win")}
          </p>
        </div>
        <p className="shrink-0 text-base font-semibold leading-normal text-white">
          {formatNumber(chances + 1)}
        </p>
      </div>
    </div>
  );
};

export default ChallengesSingle;
